// Authorization Service for Multi-App Platform
import { APPLICATIONS, SECURITY_GROUPS } from '../config/appConfig';

export class AuthorizationService {
  constructor(authService) {
    this.authService = authService;
    this.userGroups = [];
    this.userPermissions = new Set();
  }

  /**
   * Initialize user permissions based on group membership
   */
  async initializeUserPermissions() {
    const userGroups = await this.authService.getUserGroups();
    this.userGroups = userGroups;

    this.userPermissions.clear();

    userGroups.forEach((groupId) => {
      const group = SECURITY_GROUPS[groupId];
      if (group && group.applications) {
        group.applications.forEach((appId) => {
          this.userPermissions.add(appId);
        });
      }
    });

    return this.userPermissions;
  }

  /**
   * Check if user has access to a specific application
   */
  hasAccessToApp(appId) {
    return this.userPermissions.has(appId);
  }

  /**
   * Get all applications the user has access to
   */
  getAccessibleApps() {
    const accessibleApps = [];
    
    Object.values(APPLICATIONS).forEach(app => {
      // Skip hidden apps from the launcher interface
      if (app.status === 'hidden') {
        return;
      }
      
      if (this.hasAccessToApp(app.id)) {
        accessibleApps.push(app);
      }
    });

    return accessibleApps.sort((a, b) => {
      // Sort by status: active first, then development, then planned
      const statusOrder = { active: 0, development: 1, planned: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
  }

  /**
   * Get user's security groups with display names (filtered to only known groups)
   */
  getUserGroupsWithNames() {
    return this.userGroups
      .filter(groupId => SECURITY_GROUPS[groupId]) // Only include groups we know about
      .map(groupId => {
        const group = SECURITY_GROUPS[groupId];
        return {
          id: groupId,
          name: group.name,
          displayName: group.displayName
        };
      });
  }

  /**
   * Check if user is platform administrator
   */
  isPlatformAdmin() {
    return this.userGroups.includes('platform-admins-group-id');
  }

  /**
   * Get application by ID
   */
  getApplication(appId) {
    // Handle both camelCase and kebab-case IDs
    const directMatch = APPLICATIONS[appId];
    if (directMatch) return directMatch;
    
    // Try to find by the application's id property
    const appByIdProperty = Object.values(APPLICATIONS).find(app => app.id === appId);
    return appByIdProperty;
  }

  /**
   * Navigate to application (with access check)
   */
  navigateToApp(appId) {
    if (!this.hasAccessToApp(appId)) {
      throw new Error(`Access denied to application: ${appId}`);
    }

    const app = this.getApplication(appId);
    if (!app) {
      throw new Error(`Application not found: ${appId}`);
    }

    // For now, navigate within the same SPA
    // Later this could be enhanced to load different micro-frontends
    window.location.hash = app.path;
    return app;
  }

  /**
   * Get platform statistics (for admin users)
   */
  getPlatformStats() {
    if (!this.isPlatformAdmin()) {
      throw new Error('Access denied: Platform administrator required');
    }

    const totalApps = Object.keys(APPLICATIONS).length;
    const activeApps = Object.values(APPLICATIONS).filter(app => app.status === 'active').length;
    const totalGroups = Object.keys(SECURITY_GROUPS).length;

    return {
      totalApplications: totalApps,
      activeApplications: activeApps,
      totalSecurityGroups: totalGroups,
      userGroups: this.userGroups.length,
      userPermissions: this.userPermissions.size
    };
  }
}

// Singleton instance
let authorizationServiceInstance = null;

export const getAuthorizationService = (authService) => {
  if (!authorizationServiceInstance) {
    authorizationServiceInstance = new AuthorizationService(authService);
  }
  return authorizationServiceInstance;
};

export const resetAuthorizationService = () => {
  authorizationServiceInstance = null;
};
